import React from 'react';
import { Link } from 'react-router-dom';
import CustomNavbar from './common/Navbar';
import Footer from './common/Footer';
import ScrollToTop from './common/ScrollToTop';
import '../assets/styles/privacyPolicy.css';
import '../assets/styles/scrollToTop.css';

const PrivacyPolicy = () => {
    return (
        <div className="privacy-page">
            <CustomNavbar />
            <div className="privacy-container">
                <div className="container">
                    <div className="privacy-content">
                            <div style={{marginBottom: '20px'}}>
                                <Link to="/" style={{color: '#3361a2', textDecoration: 'none', fontSize: '16px'}}>
                                    ← Back to Home
                                </Link>
                            </div>

                        <h1>Privacy Policy</h1>
                        <p className="effective-date"><strong>Effective Date:</strong> October 16, 2025</p>
                        <p className="last-updated"><strong>Last Updated:</strong> October 16, 2025</p>

                        <section className="privacy-section">
                            <h2>1. Introduction</h2>
                            <p>
                                Welcome to PacDocSign ("we," "us," or "our"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our document signing platform available at pacdocsign.com (the "Platform").
                            </p>
                            <p>
                                By accessing or using our Platform, you agree to the terms of this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access the Platform.
                            </p>
                        </section>

                        <section className="privacy-section">
                            <h2>2. Information We Collect</h2>
                            
                            <h3>2.1 Personal Information</h3>
                            <p>We collect the following types of personal information from our users:</p>
                            
                            <p><strong>For All Users:</strong></p>
                            <ul>
                                <li>Full name (first and last name)</li>
                                <li>Email address</li>
                                <li>Phone numbers (mobile, home, work)</li>
                                <li>Physical address (street, city, state, ZIP code)</li>
                                <li>Username and password (securely hashed)</li>
                                <li>Account type and role information</li>
                                <li>IP address and device information</li>
                            </ul>

                            <p><strong>For Notary Signers:</strong></p>
                            <ul>
                                <li>Company name (if applicable)</li>
                                <li>Emergency contact information (name and phone number)</li>
                                <li>Certification information (NNA certified, NSA member number)</li>
                                <li>Payment address</li>
                                <li>Professional preferences and notes</li>
                                <li>Geographic location data (latitude/longitude for service area)</li>
                                <li>Language preferences</li>
                                <li>Loan signing document preferences</li>
                                <li>Communication preferences</li>
                            </ul>

                            <p><strong>For Client Portal Users:</strong></p>
                            <ul>
                                <li>Company information and related company associations</li>
                                <li>Work extension and fax numbers</li>
                                <li>Contact preferences</li>
                                <li>Team management settings</li>
                                <li>Privilege levels and access permissions</li>
                            </ul>

                            <h3>2.2 Documents and Content</h3>
                            <p>We collect and process:</p>
                            <ul>
                                <li>Documents uploaded for signing purposes</li>
                                <li>Digital signatures and signature timestamps</li>
                                <li>Document metadata (file names, upload dates, modification history)</li>
                                <li>Document encryption keys (customer-supplied)</li>
                                <li>Communications and notes related to signing orders</li>
                                <li>Accounting and financial records related to services</li>
                            </ul>

                            <h3>2.3 Communication Data</h3>
                            <ul>
                                <li>SMS messages (with your explicit consent)</li>
                                <li>Email communications</li>
                                <li>In-platform messages and notifications</li>
                                <li>Customer support interactions</li>
                                <li>Communication preferences and history</li>
                            </ul>

                            <h3>2.4 Automatically Collected Information</h3>
                            <p>When you access our Platform, we automatically collect:</p>
                            <ul>
                                <li>Browser type and version</li>
                                <li>Operating system</li>
                                <li>Access times and dates</li>
                                <li>Pages viewed and interactions</li>
                                <li>IP addresses for security and audit purposes</li>
                                <li>Session information and authentication tokens</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>3. How We Use Your Information</h2>

                            <h3>3.1 Service Provision</h3>
                            <ul>
                                <li>Create and manage user accounts</li>
                                <li>Process and facilitate document signing transactions</li>
                                <li>Match clients with appropriate notary signers based on location and preferences</li>
                                <li>Generate reports and analytics for clients</li>
                                <li>Process payments and maintain financial records</li>
                                <li>Track document access and modifications for audit purposes</li>
                            </ul>

                            <h3>3.2 Communication</h3>
                            <ul>
                                <li>Send service-related notifications and updates</li>
                                <li>Provide customer support</li>
                                <li>Send SMS notifications (only with your explicit consent via Plivo)</li>
                                <li>Send email notifications via SendGrid and Office365</li>
                                <li>Share important account or order updates</li>
                            </ul>

                            <h3>3.3 Platform Improvement</h3>
                            <ul>
                                <li>Analyze usage patterns to improve our services</li>
                                <li>Debug and resolve technical issues</li>
                                <li>Develop new features and functionality</li>
                                <li>Conduct quality assurance and testing</li>
                            </ul>

                            <h3>3.4 Security and Compliance</h3>
                            <ul>
                                <li>Verify user identity and prevent fraud</li>
                                <li>Enforce our Terms of Service</li>
                                <li>Comply with legal obligations and regulatory requirements</li>
                                <li>Protect against unauthorized access or illegal activities</li>
                                <li>Maintain audit trails of document activity</li>
                            </ul>

                            <h3>3.5 Business Operations</h3>
                            <ul>
                                <li>Process accounting and financial transactions through QuickBooks integration</li>
                                <li>Generate business reports and analytics</li>
                                <li>Maintain accurate business records</li>
                                <li>Facilitate communication between parties</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>4. SMS Consent and Communication</h2>
                            
                            <h3>4.1 SMS Opt-In</h3>
                            <p>We collect and store your SMS consent preferences. You may receive SMS notifications about:</p>
                            <ul>
                                <li>Order confirmations and reminders</li>
                                <li>Time-sensitive signing appointments</li>
                                <li>Critical account notifications</li>
                            </ul>

                            <h3>4.2 SMS Service Provider</h3>
                            <p>SMS messages are delivered through Plivo, our SMS service provider.</p>

                            <h3>4.3 Opt-Out Rights</h3>
                            <p>You can opt out of SMS communications at any time by:</p>
                            <ul>
                                <li>Updating your profile preferences</li>
                                <li>Replying "STOP" to any SMS message</li>
                                <li>Contacting our support team</li>
                            </ul>

                            <h3>4.4 SMS Data Retention</h3>
                            <p>We retain records of your SMS consent status and the date of consent for compliance purposes.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>5. Third-Party Services</h2>
                            <p>We use the following third-party services to operate our Platform:</p>

                            <h3>5.1 Cloud Infrastructure</h3>
                            <ul>
                                <li><strong>Google Cloud Platform (GCP):</strong> Cloud hosting, serverless functions, and database infrastructure</li>
                                <li><strong>Google Cloud Storage:</strong> Secure document storage with customer-supplied encryption keys</li>
                                <li><strong>Firebase:</strong> User authentication and session management</li>
                                <li><strong>Amazon Web Services (AWS RDS):</strong> MySQL database hosting</li>
                            </ul>

                            <h3>5.2 Communication Services</h3>
                            <ul>
                                <li><strong>SendGrid:</strong> Primary email delivery service</li>
                                <li><strong>Office365 SMTP:</strong> Email notifications and communications</li>
                                <li><strong>Plivo:</strong> SMS messaging services (with user consent)</li>
                                <li><strong>Slack:</strong> Internal team notifications (does not involve user data)</li>
                            </ul>

                            <h3>5.3 Location Services</h3>
                            <ul>
                                <li><strong>Google Places API:</strong> Address validation and location-based signer matching</li>
                            </ul>

                            <h3>5.4 Accounting Integration</h3>
                            <ul>
                                <li><strong>QuickBooks:</strong> Financial accounting and payment processing integration</li>
                            </ul>

                            <h3>5.5 Service Provider Protections</h3>
                            <p>All third-party service providers are contractually bound to:</p>
                            <ul>
                                <li>Use your information solely to provide services to us</li>
                                <li>Maintain the confidentiality and security of your information</li>
                                <li>Comply with applicable privacy and security laws, including GLBA requirements</li>
                                <li>Refrain from using your information for their own marketing or other purposes</li>
                                <li>Implement appropriate security safeguards</li>
                            </ul>
                            <p>While these providers have their own privacy policies, they are prohibited from using your nonpublic personal information for any purpose other than providing services to PacDocSign.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>6. Data Security</h2>
                            
                            <h3>6.1 Security Measures</h3>
                            <p>We implement industry-standard security measures to protect your information:</p>
                            <ul>
                                <li>Encryption of data in transit (SSL/TLS)</li>
                                <li>Encryption of data at rest</li>
                                <li>Secure password hashing</li>
                                <li>Multi-factor authentication options</li>
                                <li>Regular security audits and monitoring</li>
                                <li>Access controls and authentication mechanisms</li>
                                <li>Secure cloud infrastructure with enterprise-grade security</li>
                            </ul>

                            <h3>6.2 Document Security</h3>
                            <ul>
                                <li>Documents are stored in Google Cloud Storage with customer-supplied encryption keys (CSEK)</li>
                                <li>Access is restricted to authorized users only</li>
                                <li>Audit trails track all document access and modifications including IP addresses</li>
                                <li>Secure deletion protocols for removed documents</li>
                                <li>Encrypted file transmission</li>
                            </ul>

                            <h3>6.3 Account Security</h3>
                            <p>Users are responsible for:</p>
                            <ul>
                                <li>Maintaining the confidentiality of their login credentials</li>
                                <li>Using strong, unique passwords</li>
                                <li>Logging out after each session on shared devices</li>
                                <li>Reporting any unauthorized access immediately</li>
                            </ul>

                            <h3>6.4 Authentication Security</h3>
                            <ul>
                                <li>We use Firebase Authentication for secure user authentication</li>
                                <li>Session tokens with automatic expiration</li>
                                <li>Refresh token mechanism for extended sessions</li>
                                <li>Two-factor authentication options available</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>7. Data Retention</h2>
                            
                            <h3>7.1 Active Accounts</h3>
                            <p>We retain your personal information for as long as your account is active or as needed to provide services.</p>

                            <h3>7.2 Inactive Accounts</h3>
                            <p>After account closure, we may retain certain information:</p>
                            <ul>
                                <li>To comply with legal obligations</li>
                                <li>To resolve disputes</li>
                                <li>To enforce our agreements</li>
                                <li>For legitimate business purposes</li>
                            </ul>

                            <h3>7.3 Document Retention</h3>
                            <ul>
                                <li>Signed documents are retained according to legal requirements and business needs</li>
                                <li>Users may request deletion of documents subject to legal retention requirements</li>
                                <li>Backup copies may be retained for disaster recovery purposes</li>
                                <li>Document activity logs are maintained for audit purposes</li>
                            </ul>

                            <h3>7.4 Communication Records</h3>
                            <ul>
                                <li>Email and SMS records are retained for operational and compliance purposes</li>
                                <li>Support communications are retained to improve service quality</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>8. Information Sharing and Disclosure</h2>
                            
                            <h3>8.1 Service Provision</h3>
                            <p>We share information with:</p>
                            <ul>
                                <li><strong>Notary Signers:</strong> Client contact information necessary for completing signing appointments</li>
                                <li><strong>Clients:</strong> Signer information and completed documents</li>
                                <li><strong>Service Partners:</strong> Information necessary to facilitate the signing process</li>
                            </ul>

                            <h3>8.2 Legal Requirements</h3>
                            <p>We may disclose information when required by law or to:</p>
                            <ul>
                                <li>Comply with legal processes (subpoenas, court orders)</li>
                                <li>Enforce our Terms of Service</li>
                                <li>Protect our rights, property, or safety</li>
                                <li>Prevent fraud or security threats</li>
                                <li>Respond to government requests</li>
                            </ul>

                            <h3>8.3 Business Transfers</h3>
                            <p>In the event of a merger, acquisition, or sale of assets, user information may be transferred to the acquiring entity.</p>

                            <h3>8.4 Third-Party Service Providers (GLBA Service Provider Exception)</h3>
                            <p>We share nonpublic personal information with third-party service providers who perform services on our behalf (hosting, email delivery, SMS messaging, accounting, etc.) under the GLBA service provider exception. These providers are:</p>
                            <ul>
                                <li>Contractually prohibited from disclosing or using your information except to carry out the purposes for which we disclosed the information</li>
                                <li>Required to maintain strict confidentiality and security protections</li>
                                <li>Subject to the same privacy and security standards we maintain</li>
                                <li>Listed in detail in Section 5 of this policy</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>9. Your Privacy Rights</h2>
                            
                            <h3>9.1 Access and Correction</h3>
                            <p>You have the right to:</p>
                            <ul>
                                <li>Access your personal information</li>
                                <li>Correct inaccurate information</li>
                                <li>Update your profile and preferences</li>
                                <li>Request a copy of your data</li>
                            </ul>

                            <h3>9.2 Data Portability</h3>
                            <p>You may request a copy of your data in a structured, commonly used format.</p>

                            <h3>9.3 Deletion Rights</h3>
                            <p>You may request deletion of your personal information, subject to:</p>
                            <ul>
                                <li>Legal retention requirements</li>
                                <li>Ongoing business needs</li>
                                <li>Contract obligations</li>
                            </ul>

                            <h3>9.4 Opt-Out Rights</h3>
                            <p>You may opt out of:</p>
                            <ul>
                                <li>Marketing communications</li>
                                <li>SMS notifications</li>
                                <li>Certain data collection practices</li>
                            </ul>

                            <h3>9.5 How to Exercise Your Rights</h3>
                            <p>Contact us at <a href="mailto:privacy@pacdocsign.com">privacy@pacdocsign.com</a> to exercise any of these rights.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>10. State-Specific Rights</h2>
                            
                            <h3>10.1 California Residents (CCPA/CPRA)</h3>
                            <p>California residents have additional rights:</p>
                            <ul>
                                <li>Right to know what personal information is collected</li>
                                <li>Right to know if personal information is sold or disclosed</li>
                                <li>Right to opt out of the sale of personal information</li>
                                <li>Right to deletion (subject to exceptions)</li>
                                <li>Right to non-discrimination for exercising privacy rights</li>
                            </ul>

                            <h3>10.2 Nevada Residents</h3>
                            <p>Nevada residents may opt out of the sale of certain covered information.</p>

                            <h3>10.3 Other State Laws</h3>
                            <p>Residents of states with comprehensive privacy laws (Virginia, Colorado, Connecticut, Utah) have similar rights as described above.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>11. Cookies and Tracking Technologies</h2>
                            
                            <h3>11.1 Cookies We Use</h3>
                            <ul>
                                <li><strong>Essential Cookies:</strong> Required for Platform functionality</li>
                                <li><strong>Authentication Cookies:</strong> Manage user sessions via Firebase</li>
                                <li><strong>Preference Cookies:</strong> Remember your settings</li>
                                <li><strong>Session Cookies:</strong> Maintain your logged-in state</li>
                            </ul>

                            <h3>11.2 Cookie Management</h3>
                            <p>You can control cookies through your browser settings. Note that disabling certain cookies may affect Platform functionality.</p>

                            <h3>11.3 Do Not Track</h3>
                            <p>We currently do not respond to Do Not Track (DNT) signals.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>12. Children's Privacy</h2>
                            <p>Our Platform is not intended for children under 18. We do not knowingly collect information from children. If we discover we have collected information from a child, we will delete it immediately.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>13. International Data Transfers</h2>
                            <p>Your information may be transferred to and processed in countries other than your country of residence, including the United States. We ensure appropriate safeguards are in place for international transfers.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>14. Changes to This Privacy Policy</h2>
                            <p>We may update this Privacy Policy periodically. We will notify you of material changes by:</p>
                            <ul>
                                <li>Posting the updated policy on our Platform</li>
                                <li>Updating the "Last Updated" date</li>
                                <li>Sending email notifications for significant changes</li>
                            </ul>
                            <p>Continued use of the Platform after changes constitutes acceptance of the updated Privacy Policy.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>15. Gramm-Leach-Bliley Act (GLBA) Compliance</h2>
                            
                            <h3>15.1 Our GLBA Commitment</h3>
                            <p>As a provider of services to financial institutions, we comply with the Gramm-Leach-Bliley Act (GLBA) and its implementing regulations, including the Safeguards Rule and the Privacy Rule.</p>

                            <h3>15.2 Categories of Nonpublic Personal Information We Collect</h3>
                            <p>We collect the following categories of nonpublic personal information:</p>
                            <ul>
                                <li><strong>Identity Information:</strong> Name, address, email, phone number, government-issued ID information</li>
                                <li><strong>Financial Transaction Information:</strong> Payment details, account numbers, transaction history</li>
                                <li><strong>Account Authentication Information:</strong> Usernames, passwords (securely hashed), security questions</li>
                                <li><strong>Document Information:</strong> Signed documents, financial records, loan documentation</li>
                                <li><strong>Professional Information:</strong> Notary certifications, business affiliations</li>
                            </ul>

                            <h3>15.3 Information Sharing Practices</h3>
                            
                            <h4>We Do Not Sell Your Information</h4>
                            <ul>
                                <li>We do not sell your nonpublic personal information to any third parties</li>
                                <li>We have never sold nonpublic personal information</li>
                                <li>We have no plans to sell nonpublic personal information in the future</li>
                            </ul>

                            <h4>Corporate Affiliates</h4>
                            <p>We do not have corporate affiliates with whom we share nonpublic personal information</p>

                            <h4>Non-Affiliated Third Parties</h4>
                            <ul>
                                <li>We do not share your nonpublic personal information with non-affiliated third parties for their marketing purposes</li>
                                <li>We do not engage in joint marketing arrangements with other financial institutions</li>
                            </ul>

                            <h3>15.4 Service Provider Exception</h3>
                            <p>We share nonpublic personal information with non-affiliated service providers solely to perform services on our behalf under the GLBA service provider exception. These providers:</p>
                            <ul>
                                <li>Are contractually prohibited from using your information for their own purposes</li>
                                <li>Are required to maintain the confidentiality and security of your information</li>
                                <li>May only use the information to perform the specific services we have engaged them for</li>
                                <li>Are listed in Section 5 of this policy and include:
                                    <ul>
                                        <li>Cloud hosting and storage providers (Google Cloud Platform, AWS)</li>
                                        <li>Communication service providers (SendGrid, Office365, Plivo)</li>
                                        <li>Authentication services (Firebase)</li>
                                        <li>Accounting integration (QuickBooks)</li>
                                    </ul>
                                </li>
                            </ul>

                            <h3>15.5 Permitted Disclosures Without Consent</h3>
                            <p>We may disclose your nonpublic personal information without providing you an opportunity to opt out in the following circumstances permitted by GLBA:</p>
                            <ul>
                                <li>To comply with federal, state, or local laws, rules, and other applicable legal requirements</li>
                                <li>To comply with a properly authorized civil, criminal, or regulatory investigation or subpoena</li>
                                <li>To respond to judicial process or government regulatory authorities</li>
                                <li>To protect against or prevent actual or potential fraud, unauthorized transactions, claims, or other liability</li>
                                <li>To institutional risk control or for resolving customer disputes or inquiries</li>
                                <li>To persons holding a legal or beneficial interest relating to the customer</li>
                                <li>To persons acting in a fiduciary or representative capacity on behalf of the customer</li>
                            </ul>

                            <h3>15.6 Former Customers</h3>
                            <p>We apply the same privacy protections to former customers as we do to current customers. Your nonpublic personal information continues to be protected under this Privacy Policy even after your relationship with us ends.</p>

                            <h3>15.7 GLBA Safeguards</h3>
                            <p>We maintain a comprehensive information security program that includes:</p>
                            
                            <h4>Administrative Safeguards:</h4>
                            <ul>
                                <li>Employee training and management</li>
                                <li>Information security policies and procedures</li>
                                <li>Regular risk assessments</li>
                            </ul>
                            
                            <h4>Technical Safeguards:</h4>
                            <ul>
                                <li>Encryption of data in transit (SSL/TLS) and at rest</li>
                                <li>Secure authentication mechanisms</li>
                                <li>Multi-factor authentication options</li>
                                <li>Access controls and monitoring</li>
                                <li>Regular security testing and monitoring</li>
                            </ul>

                            <h4>Physical Safeguards:</h4>
                            <ul>
                                <li>Secure cloud infrastructure with enterprise-grade physical security</li>
                                <li>Controlled access to systems and data</li>
                                <li>Secure disposal of records containing nonpublic personal information</li>
                            </ul>

                            <h3>15.8 Opt-Out Rights</h3>
                            <p>You have the right to opt out of certain information sharing practices. However, because:</p>
                            <ul>
                                <li>We do not sell your information</li>
                                <li>We do not share with non-affiliated third parties for marketing purposes</li>
                                <li>We do not have corporate affiliates</li>
                                <li>We do not engage in joint marketing</li>
                            </ul>
                            <p>There are currently no opt-out rights to exercise regarding information sharing. If our practices change, we will provide you with a new privacy notice and opt-out rights as required by law.</p>

                            <h3>15.9 Annual Privacy Notice</h3>
                            <p>As required by GLBA, we provide this privacy notice to our customers. If our information sharing practices change in a way that affects you, we will provide you with a revised notice.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>16. Contact Information</h2>
                            
                            <h3>Questions or Concerns</h3>
                            <p>If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
                            <div className="contact-info">
                                <p><strong>Email:</strong> <a href="mailto:privacy@pacdocsign.com">privacy@pacdocsign.com</a></p>
                                <p><strong>Website:</strong> <a href="https://pacdocsign.com">https://pacdocsign.com</a></p>
                                <p><strong>Mail:</strong> Pacific Document Signing Inc.</p>
                                <p>2154 Westwood Blvd. Suite #300</p>
                                <p>Los Angeles, CA 90025</p>
                                <p><strong>Tel:</strong> <a href="tel:800-884-7587">800-884-7587</a></p>
                                <p><strong>Fax:</strong> 800-732-4494</p>
                            </div>

                            <h3>Data Protection Officer</h3>
                            <p>For privacy-related inquiries, you may contact our Data Protection Officer at <a href="mailto:dpo@pacdocsign.com">dpo@pacdocsign.com</a></p>

                            <h3>Response Time</h3>
                            <p>We aim to respond to all privacy inquiries within 30 days.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>17. Specific Service Disclosures</h2>
                            
                            <h3>17.1 Document Processing</h3>
                            <ul>
                                <li>Documents are uploaded and stored in Google Cloud Storage</li>
                                <li>Documents are encrypted using customer-supplied encryption keys</li>
                                <li>Document activity is tracked including user actions, timestamps, and IP addresses</li>
                                <li>Only PDF files are accepted for upload</li>
                            </ul>

                            <h3>17.2 Location Services</h3>
                            <ul>
                                <li>Notary signers may provide location data (latitude/longitude) for service area mapping</li>
                                <li>Google Places API is used for address validation</li>
                                <li>Location data helps match signers with nearby clients</li>
                                <li>Location sharing is voluntary and can be disabled</li>
                            </ul>

                            <h3>17.3 Emergency Contacts</h3>
                            <ul>
                                <li>Emergency contact information is stored for signer safety</li>
                                <li>This information is only accessed in emergency situations</li>
                                <li>Emergency contacts are not contacted for marketing purposes</li>
                            </ul>

                            <h3>17.4 Automated Appointment Reminders</h3>
                            <ul>
                                <li>SMS reminders are sent automatically based on appointment times</li>
                                <li>Reminders are only sent to users who have consented to SMS communications</li>
                                <li>Multiple phone numbers are used to manage message delivery</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>18. Data Breach Notification</h2>
                            <p>In the event of a data breach affecting your personal information, we will:</p>
                            <ul>
                                <li>Notify affected users within 72 hours of discovery</li>
                                <li>Provide details about the breach and affected data</li>
                                <li>Explain steps we're taking to address the breach</li>
                                <li>Advise you on protective measures you can take</li>
                                <li>Comply with all applicable data breach notification laws</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>19. Automated Decision Making</h2>
                            <p>We may use automated systems to:</p>
                            <ul>
                                <li>Match clients with appropriate notary signers based on location and preferences</li>
                                <li>Send automated appointment reminders via SMS and email</li>
                                <li>Detect unusual account activity</li>
                                <li>Optimize service delivery</li>
                            </ul>
                            <p>You have the right to request human review of automated decisions that significantly affect you.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>20. Business Account Privacy</h2>
                            
                            <h3>20.1 Corporate Accounts</h3>
                            <ul>
                                <li>Account administrators can manage user access</li>
                                <li>Administrators may view user activity within their organization</li>
                                <li>Company data is segregated from personal accounts</li>
                            </ul>

                            <h3>20.2 Data Ownership</h3>
                            <ul>
                                <li>Businesses retain ownership of their uploaded documents</li>
                                <li>We act as a data processor for business account data</li>
                                <li>Business account agreements may include additional privacy terms</li>
                            </ul>
                        </section>

                        <section className="privacy-section acknowledgment">
                            <h2>Acknowledgment</h2>
                            <p><strong>By using PacDocSign, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.</strong></p>
                            <p><strong>Version:</strong> 1.0</p>
                            <p><strong>Document ID:</strong> PP-PACDOCSIGN-2025-001</p>
                        </section>
                    </div>
                </div>
            </div>
            <Footer />
            <ScrollToTop />
        </div>
    );
};

export default PrivacyPolicy;